import { useNavigate } from 'react-router-dom';
import { CalendarDays, Users } from 'lucide-react';
import { GroupType } from '../../types/groups';
import type { GroupDto } from '../../types/groups';

interface GroupCardProps {
  group: GroupDto;
}

const groupTypeStyles: Record<string, string> = {
  [GroupType.Project]: 'bg-blue-50 text-blue-700 ring-blue-200',
  [GroupType.CustomerCall]: 'bg-purple-50 text-purple-700 ring-purple-200',
  [GroupType.Problem]: 'bg-orange-50 text-orange-700 ring-orange-200',
};

function formatCreatedDate(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

export default function GroupCard({ group }: GroupCardProps) {
  const navigate = useNavigate();
  const groupName = group.groupName?.trim() || 'Untitled List';
  const description = group.description?.trim() || 'No description available.';
  const groupType = group.groupType?.trim() || GroupType.Project;
  const createdBy = group.createdByUser?.name?.trim() || 'Unknown User';
  const memberCount = group.members?.length || 0;
  const groupTypeClass = groupTypeStyles[groupType] || 'bg-slate-100 text-slate-700 ring-slate-200';

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={() => navigate(`/groups/${group.groupId}`)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          navigate(`/groups/${group.groupId}`);
        }
      }}
      className="cursor-pointer rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition duration-200 hover:border-blue-100 hover:bg-blue-50/30 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500/30 sm:p-5"
      aria-label={`Open List ${groupName}`}
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="flex min-w-0 flex-col gap-4 sm:flex-row md:flex-1">
          <div className="flex h-20 w-20 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-blue-50 text-blue-700 ring-1 ring-blue-100">
            <Users className="h-9 w-9" />
          </div>

          <div className="min-w-0 flex-1">
            <div className="mb-2 flex min-w-0 items-center gap-2">
              <h2 className="mb-0 min-w-0 truncate text-lg font-bold text-gray-900">{groupName}</h2>
              <span className={`flex-shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${groupTypeClass}`}>
                {groupType}
              </span>
            </div>

            <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-600 ">
              <span>Created by {createdBy}</span>
              <span className="inline-flex items-center gap-1.5">
                <CalendarDays className="h-4 w-4 text-gray-400" />
                Created on {formatCreatedDate(group.createdAt)}
              </span>
            </div>

            <p className="mb-0 line-clamp-2 text-sm leading-6 text-gray-600">{description}</p>
          </div>
        </div>

        <div className="flex flex-shrink-0 flex-col gap-4 md:items-end">
          <div className="text-left md:text-right">
            <p className="mb-0 whitespace-nowrap text-sm font-semibold text-gray-900">
              {memberCount} {memberCount === 1 ? 'Member' : 'Members'}
            </p>
          </div>
        </div>
      </div>
    </article>
  );
}
